// Address search + reverse geocoding, via Esri's ArcGIS World Geocoder.
//
// Why Esri and not OpenStreetMap/Nominatim: the client's corporate network (FortiGate) SSL-intercepts and
// BLOCKS openstreetmap.org / google — the same reason the basemap tiles use Esri (see lib/mapTiles.ts).
// Esri (arcgis.com) IS reachable in that environment. `findAddressCandidates` and `reverseGeocode` work
// anonymously (no API key) for interactive, non-stored use. If search ever returns nothing on the client
// network, their IT needs to allow `geocode.arcgis.com` alongside the already-allowed Esri tile host.

const BASE = 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';
const OUT_FIELDS = 'Match_addr,LongLabel,StAddr,Address,Neighborhood,District,City,Subregion,Region,Postal';

/** The address parts we push into a form (state is a NAME, e.g. "Maharashtra", mapped to a GST code by the caller). */
export interface GeoParts {
  address: string;
  city: string;
  pincode: string;
  state: string;
}

/** A search suggestion: the display label + coordinates + its resolved address parts. */
export interface GeoResult extends GeoParts {
  label: string;
  lat: number;
  lng: number;
}

// Esri address attributes → our fields. Rural India often has an empty street (`Address`), so fall back to
// the neighbourhood/district, then the full match label, so the line is never blank when a place is known.
function partsFromAttrs(a: any): GeoParts {
  const line = String(a?.Address || a?.StAddr || '').trim()
    || [a?.Neighborhood, a?.District].filter(Boolean).join(', ')
    || String(a?.Match_addr || a?.LongLabel || '').trim();
  return {
    address: line,
    city: a?.City || a?.District || a?.Subregion || '',
    pincode: a?.Postal || '',
    state: a?.Region || '',
  };
}

/** Forward geocode a free-text query (as-you-type friendly) → up to 6 India candidates. */
export async function geocodeSearch(term: string, signal?: AbortSignal): Promise<GeoResult[]> {
  const url = `${BASE}/findAddressCandidates?f=json&singleLine=${encodeURIComponent(term)}`
    + `&countryCode=IND&maxLocations=6&outFields=${encodeURIComponent(OUT_FIELDS)}`;
  const r = await fetch(url, { signal });
  const j: any = await r.json();
  const candidates: any[] = Array.isArray(j?.candidates) ? j.candidates : [];
  return candidates
    .map((c) => {
      const a = c.attributes ?? {};
      return {
        label: c.address || a.Match_addr || term,
        lat: Number(c.location?.y),
        lng: Number(c.location?.x),
        ...partsFromAttrs(a),
      };
    })
    .filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lng));
}

/** Reverse geocode coordinates → a display label + address parts. Null when nothing is found. */
export async function geocodeReverse(lat: number, lng: number, signal?: AbortSignal): Promise<({ label: string } & GeoParts) | null> {
  const url = `${BASE}/reverseGeocode?f=json&location=${lng},${lat}&outFields=*&langCode=en`;
  const r = await fetch(url, { signal });
  const j: any = await r.json();
  const a = j?.address;
  if (!a) return null;
  return { label: a.Match_addr || a.LongLabel || '', ...partsFromAttrs(a) };
}
